import {Injectable, signal} from '@angular/core';
import {Utils} from '@/classes/utils';
import {TactaCanvas} from '@/_services/tacta-card.service';

export class Player {
  name: string;
  cards: number[] = [];

  game(idx: number) {
    return new GameConfig(this.cards[idx], -1, '');
  }
}

export class CardConfig {
  static scale = 5;
  static defWidth = 64;
  static defHeight = 89;
  static defBorder = 1.5;
  colors: { s: string, f: string, sm: string, fm: string } = {
    s: '#ffffff', f: '#000000', sm: '#ffff00', fm: '#aaaaaa'
  };
  suite: number;
  cardId: number;
  flipped = false;
  linkedCards: { card: CardConfig, link: any }[] = [];

  _areas: string;

  get areas(): string {
    if (!this.flipped) {
      return this._areas ?? '';
    }
    const flips = 'badcfehgilkjmponrq';
    let ret = '';
    for (let i = 0; i < this._areas.length; i += 2) {
      ret += flips[this._areas.charCodeAt(i) - 97] + this._areas[i + 1];
    }
    return ret;
  }

  set areas(value: string) {
    this._areas = value;
  }

  connect(card: CardConfig, link: any) {
    if (link == null) {
      console.log('link is null', this, card);
      return;
    }
    this.linkedCards.push({card: card, link: link});
  }
}

export class GameConfig {
  linkedCards: GameConfig[] = [];

  constructor(public cardIdx: number,
              public turnIdx: number,
              public parentLink?: string) {
  }

  get asString(): string {
    const ret: string[] = [];
    ret.push(`${Utils.hex(this.cardIdx)}${Utils.hex(this.turnIdx)}${this.parentLink ?? '--'}[`);
    for (const link of this.linkedCards) {
      ret.push(link.asString);
    }
    ret.push(']');
    return Utils.join(ret, '');
  }

  static fromString(src: string, games: GameConfig[], i = 0): number {
    while (i < src.length && src[i] !== ']') {
      const cardIdx = parseInt(src.substring(i, i + 2), 16);
      const turnIdx = parseInt(src.substring(i + 2, i + 4), 16);
      const parentLink = src.substring(i + 4, i + 6);
      const ret = new GameConfig(cardIdx, turnIdx, parentLink);
      games.push(ret);
      i += 8;
      while (src[i - 1] !== ']') {
        i = GameConfig.fromString(src, ret.linkedCards, i - 1);
      }
    }
    return i + 1;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TactaService {
  colors: any = {
    0: {s: 'white', f: '#000000', sm: '#ffff00', fm: '#aaaaaa'},
    1: {s: 'white', f: '#e59739', sm: '#ffff00', fm: '#aaaaaa'},
    2: {s: 'white', f: '#da3925', sm: '#ffff00', fm: '#aaaaaa'},
    3: {s: 'white', f: '#df3ec0', sm: '#ffff00', fm: '#aaaaaa'},
    4: {s: 'white', f: '#4535d4', sm: '#ffff00', fm: '#aaaaaa'},
    5: {s: 'white', f: '#77b4f8', sm: '#ffff00', fm: '#aaaaaa'},
    6: {s: 'white', f: '#8adb5a', sm: '#ffff00', fm: '#aaaaaa'}
  };

  deck: CardConfig[];
  players: Player[];

  games: GameConfig[];
  connectors: any = {
    ai: {x: 58, y: -6, deg: -135},
    aj: {x: 11, y: 0, deg: -45},
    ak: {x: 0, y: 22, deg: 45},
    al: {x: -11, y: 0, deg: 135},
    bi: {x: 71, y: -73, deg: 135},
    bj: {x: 82, y: 51, deg: -135},
    bk: {x: -70, y: 73, deg: -45},
    bl: {x: -82, y: -51, deg: 45},
    ci: {x: 28, y: -143, deg: 45},
    cj: {x: 180, y: 20, deg: 135},
    ck: {x: -28, y: 143, deg: 225},
    cl: {x: -180, y: -20, deg: -45},
    di: {x: -98, y: -93, deg: -45},
    dj: {x: 109, y: -71, deg: 45},
    dk: {x: 98, y: 93, deg: 135},
    dl: {x: -110, y: 71, deg: -135},
    ee: {x: 0, y: -13, deg: 180},
    ef: {x: 0, y: -12, deg: -90},
    eg: {x: 0, y: 13, deg: 0},
    eh: {x: 0, y: 12, deg: 90},
    fe: {x: 0, y: -84, deg: 90},
    ff: {x: 100, y: -13, deg: 180},
    fg: {x: 0, y: 84, deg: -90},
    fh: {x: -100, y: 13, deg: 0},
    ge: {x: -100, y: -112, deg: 0},
    gf: {x: 137, y: -84, deg: 90},
    gg: {x: 100, y: 112, deg: 180},
    gh: {x: -137, y: 84, deg: -90},
    he: {x: -137, y: -12, deg: -90},
    hf: {x: 0, y: -112, deg: 0},
    hg: {x: 137, y: 12, deg: 90},
    hh: {x: 0, y: 112, deg: 180},
    ia: {x: 13, y: -56, deg: -225},
    ib: {x: 58, y: -5, deg: 225},
    ic: {x: -13, y: 56, deg: -45},
    id: {x: -58, y: 5, deg: 45},
    ja: {x: -44, y: -117, deg: 45},
    jb: {x: 142, y: -46, deg: 135},
    jc: {x: 44, y: 117, deg: -135},
    jd: {x: -142, y: 46, deg: -45},
    ka: {x: -44, y: -117, deg: 45},
    kb: {x: 142, y: -46, deg: 135},
    kc: {x: 44, y: 117, deg: -135},
    kd: {x: -142, y: 46, deg: -45},
    la: {x: -72, y: 5, deg: 225},
    lb: {x: -26, y: -66, deg: -45},
    lc: {x: 72, y: -5, deg: 45},
    ld: {x: 26, y: 66, deg: -225},
    mm: {x: 50, y: -32, deg: 180},
    mn: {x: 24, y: 36, deg: 270},
    mo: {x: -50, y: 32, deg: 0},
    mp: {x: -24, y: -36, deg: 90},
    nm: {x: 69, y: -104, deg: 90},
    no: {x: -69, y: 104, deg: -90},
    om: {x: -50, y: -132, deg: 0},
    on: {x: 163, y: -36, deg: 90},
    oo: {x: 50, y: 132, deg: 180},
    op: {x: -163, y: 36, deg: -90},
    pm: {x: -70, y: -32, deg: -90},
    po: {x: 70, y: 32, deg: 90},
    qq: {x: 125, y: 50, deg: 180},
    qr: {x: -125, y: -50, deg: 0},
    rq: {x: 25, y: -50, deg: 0},
    rr: {x: -25, y: 50, deg: 180}
  };
  cvsList: TactaCanvas[] = [];
  // global signal to refresh all instances of TactaCard
  markedCanvas = signal<TactaCanvas>(null);

  get gamesAsString(): string {
    return this.games?.reduce((a, b) => a + b.asString, '') ?? '';
  }

  card(data: any) {
    const ret = new CardConfig();
    ret.colors = this.colors[data.color ?? 0];
    ret.suite = data.suite ?? 0;
    ret.areas = data.areas ?? 'a-b-c-d-e-f-g-h-i-j-k-l-m-n-o-p-q-r-';
    ret.cardId = data.cardId ?? this.deck.length;
    return ret;
  }

  addToDeck(src: any) {
    this.deck.push(this.card({...src, cardId: this.deck.length}));
  }

  createDeck() {
    CardConfig.scale = 2;
    this.deck = [];
    this.addToDeck({color: 0, suite: 4});
    for (let i = 1; i <= 6; i++) {
      this.addToDeck({color: i, suite: 0, areas: 'l1f0o0'});
      this.addToDeck({color: i, suite: 0, areas: 'd1j1e0'});
      this.addToDeck({color: i, suite: 0, areas: 'b0l0o3'});
      this.addToDeck({color: i, suite: 0, areas: 'a0d0q4'});
      this.addToDeck({color: i, suite: 0, areas: 'c1i1p3'});
      this.addToDeck({color: i, suite: 0, areas: 'i1j1l1o3'});
      this.addToDeck({color: i, suite: 3, areas: 'd1e0n0'});
      this.addToDeck({color: i, suite: 3, areas: 'd1i1j0'});
      this.addToDeck({color: i, suite: 3, areas: 'b0k0p3'});
      this.addToDeck({color: i, suite: 3, areas: 'b1l0o3'});
      this.addToDeck({color: i, suite: 3, areas: 'j1r4'});
      this.addToDeck({color: i, suite: 3, areas: 'a1d1q4'});
      this.addToDeck({color: i, suite: 4, areas: 'b1c0p0'});
      this.addToDeck({color: i, suite: 4, areas: 'f2l0o0'});
      this.addToDeck({color: i, suite: 4, areas: 'c1f2l0'});
      this.addToDeck({color: i, suite: 4, areas: 'd0i1n3'});
      this.addToDeck({color: i, suite: 4, areas: 'e2j0o3'});
      this.addToDeck({color: i, suite: 4, areas: 'd0e2q4'});
    }
    this.addToDeck({color: 0, suite: 0, areas: 'a0b0c0d0'});
    this.addToDeck({color: 0, suite: 0, areas: 'e0f0g0h0'});
    this.addToDeck({color: 0, suite: 3, areas: 'i0j0k0l0'});
    this.addToDeck({color: 0, suite: 4, areas: 'm0n0o0p0'});
    this.addToDeck({color: 0, suite: 4, areas: 'q0r0'});
    this.games = [];
    let src = '';
    let idx = 0;
    for (let i = 0; i < this.deck.length; i++) {
      src += `${Utils.hex(i)}${Utils.hex(idx++)}--[]`;
    }
    // src = '0000--[6201ic[]5a02qr[5b03fe[5004dl[]]]6c05ef[5406qq[6807la[5208oo[]]]6a09id[]]]';
    src = '0000--[]';
    GameConfig.fromString(src, this.games);
  }

  // scaleDeck() {
  //   for (const card of this.deck) {
  //     card.width = 64 * CardConfig.scale;
  //     card.height = 89 * CardConfig.scale;
  //     card.border = 1.5 * CardConfig.scale;
  //   }
  // }

  createPlayer(name: string, color = -1) {
    this.players ??= [];
    let start: number;
    const colors = [];
    if (color < 0 || this.players.some(p => p.cards.includes((color + 1) * 18))) {
      // collect colors not given to any player
      for (let i = 1; i < 7; i++) {
        if (!this.players.some(p => p.cards.includes(i * 18))) {
          colors.push(i - 1);
        }
      }
      // pick one color
      start = colors[Math.floor(Math.random() * colors.length)] * 18 + 1;
    } else {
      start = color * 18 + 1;
    }
    const player = new Player();
    player.name = name;
    player.cards = Utils.randomNumbers(18, start);
    this.players.push(player);
    console.log(name, colors, start, player.cards);
  }

  createTactaCanvas(src: any) {
    const ret = new TactaCanvas(src, this);
    this.cvsList.push(ret);
    return ret;
  }

  clearMarkedAreas() {
    for (const cvs of this.cvsList) {
      cvs.markedAreas = '';
    }
  }

  adjustDimensions(cvs: TactaCanvas, values: any) {
    cvs.cardWidth = values.cardWidth;
    cvs.cardHeight = values.cardHeight;
    cvs.cardBorder = values.cardBorder;
    for (const card of cvs.cards) {
      this.adjustDimensions(card, values);
    }
  }

  moveMarkedToBoard(cvs: TactaCanvas, area: string, cards: TactaCanvas[]) {
    this.markedCanvas().parentLink = `${this.markedCanvas().markedAreas}${area}`;
    this.markedCanvas().ctx = cvs.ctx;
    this.markedCanvas().cardWidth = cvs.cardWidth;
    this.markedCanvas().cardHeight = cvs.cardHeight;
    this.markedCanvas().cardBorder = cvs.cardBorder;
    cards.push(this.markedCanvas());
    for (const player of this.players) {
      player.cards = player.cards.filter(c => c !== this.markedCanvas().cardIdx);
    }
    this.markedCanvas.set(null);
    this.clearMarkedAreas();
  }
}
